/**
 * AcenteGroupProgram — printable "Grup Programı" (group itinerary) template.
 *
 * Purpose
 * -------
 * Agencies frequently hand out a paper program to their pilgrim groups.
 * Instead of maintaining a Word template outside the panel, this page lets
 * them build the program directly here and hit print/PDF.
 *
 * Data model (all client-side; not persisted to backend — this is a
 * one-off document generator, not a stored entity):
 *   header : { agency_logo?, group_name, hotel_name, guide_name, guide_phone,
 *              checkin, checkout, transport_note }
 *   days   : [{ date, title?, items:[{ time, activity, note }] }]
 *
 * "Rezervasyondan Doldur" prefills header + days from an existing reservation
 * so agencies don't retype dates/hotel — saving ~2 minutes per group.
 *
 * Print
 * -----
 * Uses browser-native window.print() with a `.print-area` scope + tailwind
 * `print:` variants declared inline. No PDF library needed.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { formatTr } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  CalendarCheck, Printer, Plus, Trash2, Copy, FileText, ClipboardList,
  Hotel, User, Phone, Bus, Wand2, ArrowUp, ArrowDown, Sparkles, Camera, Loader2,
  Moon, Star, X, CheckCircle2, Image as ImageIcon,
} from "lucide-react";

const DEFAULT_SLOTS = [
  { time: "10:00", activity: "Umre farizası", note: "" },
  { time: "16:00", activity: "Mescid-i Nebevi ziyareti", note: "" },
];

function ymdBetween(startYmd, endYmd) {
  if (!startYmd || !endYmd) return [];
  const out = [];
  const s = new Date(startYmd + "T00:00");
  const e = new Date(endYmd + "T00:00");
  if (isNaN(s) || isNaN(e) || s > e) return [];
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

function defaultHeader() {
  return {
    group_name: "",
    hotel_name: "",
    guide_name: "",
    guide_phone: "",
    hotel_rep_name: "",
    hotel_rep_phone: "",
    checkin: "",
    checkout: "",
    transport_note: "",
  };
}

export default function AcenteGroupProgram() {
  // Preserve typed content across accidental refresh — high-value UX for
  // agencies who spend 5+ minutes building a program. Saved per browser,
  // scoped by localStorage key. Cleared explicitly via the "Sıfırla" button.
  const STORAGE_KEY = "ariaf_group_program_draft_v1";

  const [header, setHeader] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw).header || defaultHeader();
    } catch {}
    return defaultHeader();
  });
  const [days, setDays] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw).days || [];
    } catch {}
    return [];
  });
  const [reservations, setReservations] = useState([]);
  const [pickedResId, setPickedResId] = useState("");
  const printRef = useRef(null);

  // AI extraction state — separate file input on this page.
  const aiFileRef = useRef(null);
  const [aiBusy, setAiBusy] = useState(false);
  // Per-file preview + upload progress. `stage` progresses through:
  //   'uploading' (real bytes → server, %)
  //   'analyzing' (server calls the vision LLM, unknown %; shown as indeterminate)
  //   'done'      (AI response applied)
  //   'error'     (upload or parse failure)
  // We do NOT show individual per-file progress since the API accepts all
  // files in one multipart request; instead we track ONE progress % for the
  // whole batch + per-file thumbnails so the user knows what they uploaded.
  const [aiPreviews, setAiPreviews] = useState([]);   // [{name, url, size}]
  const [aiPct, setAiPct] = useState(0);
  const [aiStage, setAiStage] = useState("idle");     // idle | uploading | analyzing | done | error

  // Toggle to show the ceremonial print preview inline on screen so the
  // agency can review before actually hitting Yazdır.
  const [showPreview, setShowPreview] = useState(false);

  // Persist draft on any change (debounced by React's own batching).
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ header, days })); } catch {}
  }, [header, days]);

  // Revoke any lingering blob preview URLs when this page unmounts so we
  // don't leak memory across sessions.
  useEffect(() => () => {
    aiPreviews.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Merge AI response into current form. Header uses fill-if-empty (protects
   * user's manual edits); days are REPLACED wholesale because a program image
   * typically supersedes whatever the user had drafted. */
  const applyAiProgram = (data) => {
    const hdr = data.header || {};
    setHeader((h) => ({
      group_name:      h.group_name      || hdr.group_name      || "",
      hotel_name:      h.hotel_name      || hdr.hotel_name      || "",
      guide_name:      h.guide_name      || hdr.guide_name      || "",
      guide_phone:     h.guide_phone     || hdr.guide_phone     || "",
      hotel_rep_name:  h.hotel_rep_name  || hdr.hotel_rep_name  || "",
      hotel_rep_phone: h.hotel_rep_phone || hdr.hotel_rep_phone || "",
      checkin:         h.checkin         || hdr.checkin         || "",
      checkout:        h.checkout        || hdr.checkout        || "",
      transport_note:  h.transport_note  || hdr.transport_note  || "",
    }));
    // Rehydrate days: keep only entries that have a valid date + at least one item.
    const cleaned = (data.days || [])
      .filter((d) => d && d.date && Array.isArray(d.items) && d.items.length > 0)
      .map((d) => ({
        date: d.date,
        title: d.title || "",
        items: d.items.map((it) => ({
          time: it.time || "",
          activity: it.activity || "",
          note: it.note || "",
        })),
      }));
    if (cleaned.length > 0) setDays(cleaned);
  };

  const handleAiUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const ok = ["image/jpeg", "image/png", "image/webp"];
    if (files.some((f) => !ok.includes(f.type))) {
      toast.error("Sadece JPEG/PNG/WEBP dosyaları desteklenir");
      e.target.value = ""; return;
    }
    if (files.some((f) => f.size > 8 * 1024 * 1024)) {
      toast.error("Her fotoğraf en fazla 8 MB olabilir");
      e.target.value = ""; return;
    }
    if (files.length > 6) {
      toast.error("En fazla 6 fotoğraf yükleyebilirsiniz");
      e.target.value = ""; return;
    }

    // Build local previews so the user sees exactly what's being processed.
    // Revoke earlier object URLs to avoid leaking blob memory across retries.
    setAiPreviews((prev) => {
      prev.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch {} });
      return files.map((f) => ({
        name: f.name,
        size: f.size,
        url: URL.createObjectURL(f),
      }));
    });

    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));

    setAiBusy(true);
    setAiStage("uploading");
    setAiPct(0);
    try {
      const r = await panelApi.post("/ai/extract-program", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180_000,
        // Real byte-level progress. Once the upload reaches 100% the server
        // is calling the vision model — we flip to an 'analyzing' stage so
        // the user knows we're not stuck.
        onUploadProgress: (ev) => {
          if (!ev.total) return;
          const pct = Math.min(99, Math.round((ev.loaded / ev.total) * 100));
          setAiPct(pct);
          if (pct >= 99) setAiStage("analyzing");
        },
      });
      const data = r?.data?.data || {};
      applyAiProgram(data);
      setAiPct(100);
      setAiStage("done");
      // Softer toast — per user request, do NOT nudge them to "check their
      // information" anymore. Just confirm the fill and move on.
      toast.success(`${files.length} fotoğraf işlendi — bilgileriniz otomatik dolduruldu`);
    } catch (err) {
      setAiStage("error");
      toast.error(err?.response?.data?.detail || "Program çıkarılamadı. Daha net bir fotoğraf deneyin.");
    } finally {
      setAiBusy(false);
      e.target.value = "";
    }
  };

  // Clear the AI preview strip (also revokes blob URLs). Called from the
  // small × button on the preview panel.
  const clearAiPreviews = () => {
    setAiPreviews((prev) => {
      prev.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch {} });
      return [];
    });
    setAiPct(0);
    setAiStage("idle");
  };

  const resetDraft = () => {
    if (!window.confirm("Tüm alanlar sıfırlanacak. Emin misiniz?")) return;
    setHeader(defaultHeader());
    setDays([]);
    setPickedResId("");
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  // Pull user's reservations for the "prefill" dropdown.
  useEffect(() => {
    panelApi.get("/reservations").then((r) => setReservations(Array.isArray(r.data) ? r.data : []))
      .catch(() => setReservations([]));
  }, []);

  const totalNights = useMemo(() => {
    if (!header.checkin || !header.checkout) return 0;
    return Math.max(0, Math.round((new Date(header.checkout) - new Date(header.checkin)) / 86400000));
  }, [header.checkin, header.checkout]);

  // Regenerate days when the date range changes but preserve any activities
  // the user already typed for dates that still fall in the new range.
  const regenerateDays = (checkin, checkout) => {
    const dates = ymdBetween(checkin, checkout);
    setDays((prev) => {
      const byDate = Object.fromEntries((prev || []).map((d) => [d.date, d]));
      return dates.map((date) => byDate[date] || { date, title: "", items: DEFAULT_SLOTS.map((s) => ({ ...s })) });
    });
  };

  const setHeaderField = (k, v) => {
    const nextHeader = { ...header, [k]: v };
    setHeader(nextHeader);
    if (k === "checkin" || k === "checkout") {
      regenerateDays(nextHeader.checkin, nextHeader.checkout);
    }
  };

  const applyReservation = (rid) => {
    setPickedResId(rid);
    const r = reservations.find((x) => x.id === rid);
    if (!r) return;
    const nextHeader = {
      ...header,
      group_name: r.group_name || header.group_name,
      hotel_name: r.hotel_name || header.hotel_name,
      checkin: r.checkin || header.checkin,
      checkout: r.checkout || header.checkout,
    };
    setHeader(nextHeader);
    regenerateDays(nextHeader.checkin, nextHeader.checkout);
    toast.success("Rezervasyon bilgileri yüklendi");
  };

  const setDayTitle = (i, title) => setDays((prev) => prev.map((d, idx) => (idx === i ? { ...d, title } : d)));
  const setItem = (dayIdx, itemIdx, patch) =>
    setDays((prev) => prev.map((d, i) => i !== dayIdx ? d : { ...d, items: d.items.map((it, j) => (j === itemIdx ? { ...it, ...patch } : it)) }));
  const addItem = (dayIdx) =>
    setDays((prev) => prev.map((d, i) => i !== dayIdx ? d : { ...d, items: [...d.items, { time: "", activity: "", note: "" }] }));
  const removeItem = (dayIdx, itemIdx) =>
    setDays((prev) => prev.map((d, i) => i !== dayIdx ? d : { ...d, items: d.items.filter((_, j) => j !== itemIdx) }));
  const moveItem = (dayIdx, itemIdx, dir) =>
    setDays((prev) => prev.map((d, i) => {
      if (i !== dayIdx) return d;
      const items = [...d.items];
      const j = itemIdx + dir;
      if (j < 0 || j >= items.length) return d;
      [items[itemIdx], items[j]] = [items[j], items[itemIdx]];
      return { ...d, items };
    }));
  const duplicateDay = (dayIdx) => {
    setDays((prev) => {
      const d = prev[dayIdx];
      if (!d) return prev;
      // Push the "template" activities onto the NEXT day (overwrite its items).
      return prev.map((x, i) => (i === dayIdx + 1 ? { ...x, items: d.items.map((it) => ({ ...it })), title: d.title || x.title } : x));
    });
    toast.success("Bir sonraki güne kopyalandı");
  };

  const applyDefaultTemplate = () => {
    if (days.length === 0) return toast.error("Önce tarih aralığını girin");
    setDays((prev) => prev.map((d) => ({ ...d, items: DEFAULT_SLOTS.map((s) => ({ ...s })) })));
    toast.success("Varsayılan program günlere uygulandı");
  };

  const handlePrint = () => {
    // Slight delay so React finalises any pending state before print.
    setTimeout(() => window.print(), 100);
  };

  return (
    <div className="space-y-6" data-testid="acente-group-program-page">
      {/* Screen-only page chrome */}
      <div className="print:hidden">
        <header>
          <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2 inline-flex items-center gap-1">
            <ClipboardList size={12} /> Yardımcı Araç
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-serif text-3xl text-brand-navy">Grup Programı Şablonu</h1>
              <p className="text-sm text-slate-600 mt-1">
                Grubunuz için günlük programı hazırlayın, sağ üstteki "Yazdır / PDF" butonundan
                çıktı alarak yolcularınıza dağıtın.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                onClick={resetDraft}
                className="text-slate-600 border-slate-200 hover:bg-slate-50"
                data-testid="program-reset"
                title="Tüm alanları sıfırla"
              >
                Sıfırla
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={applyDefaultTemplate}
                className="border-brand-orange/30 hover:bg-brand-orange/10"
                data-testid="program-apply-default"
              >
                <Wand2 size={14} className="mr-1.5" /> Varsayılan Şablonu Uygula
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPreview((v) => !v)}
                className="border-brand-navy/30 hover:bg-brand-navy/5"
                data-testid="program-toggle-preview"
              >
                {showPreview ? "Düzenlemeye Dön" : "Şablonu Önizle"}
              </Button>
              <Button
                type="button"
                onClick={handlePrint}
                disabled={days.length === 0}
                className="bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-60"
                data-testid="program-print"
              >
                <Printer size={14} className="mr-1.5" /> Yazdır / PDF
              </Button>
            </div>
          </div>
        </header>

        {/* AI Photo upload — hero shortcut */}
        <div
          className="rounded-2xl border border-brand-orange/30 bg-gradient-to-br from-brand-orange/10 via-white to-brand-gold/10 p-4 sm:p-5 shadow-sm mt-4"
          data-testid="program-ai-upload-card"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-orange to-brand-orange-dark text-white flex items-center justify-center shadow-md flex-shrink-0">
              <Sparkles size={20} />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold">Yapay Zeka Yardımcısı</div>
              <div className="font-serif text-lg text-brand-navy mt-0.5">Fotoğraftan programı otomatik doldur</div>
              <p className="text-xs text-slate-600 mt-1">
                Elinizdeki not, ekran görüntüsü ya da eski program kağıdının fotoğrafını yükleyin —
                sistem başlıkları, tarihleri ve günlük aktiviteleri otomatik olarak doldursun.
              </p>
              <p className="text-[11px] text-brand-orange-dark mt-1.5 font-medium inline-flex items-center gap-1">
                <ImageIcon size={11}/> Aynı anda 6 fotoğrafa kadar seçebilirsiniz (Ctrl / ⌘ ile çoklu seçim).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={aiFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                hidden
                onChange={handleAiUpload}
                data-testid="program-ai-input"
              />
              <Button
                type="button"
                onClick={() => aiFileRef.current?.click()}
                disabled={aiBusy}
                className="bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-70"
                data-testid="program-ai-upload-btn"
              >
                {aiBusy ? <><Loader2 size={14} className="mr-2 animate-spin" /> AI çalışıyor…</> : <><Camera size={14} className="mr-2" /> Fotoğraf(lar) Yükle</>}
              </Button>
            </div>
          </div>

          {/* Progress + previews strip. Rendered only when the user has
              picked files at least once. Auto-hides when idle after clear. */}
          {aiPreviews.length > 0 && (
            <div
              className="mt-4 rounded-xl bg-white border border-brand-orange/30 p-3"
              data-testid="program-ai-progress"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs font-semibold text-brand-navy inline-flex items-center gap-1">
                  {aiStage === "done" ? (
                    <><CheckCircle2 size={13} className="text-emerald-600"/> {aiPreviews.length} fotoğraf işlendi</>
                  ) : aiStage === "uploading" ? (
                    <><Loader2 size={13} className="animate-spin text-brand-orange"/> Fotoğraflar yükleniyor — {aiPct}%</>
                  ) : aiStage === "analyzing" ? (
                    <><Sparkles size={13} className="text-brand-orange animate-pulse"/> Yapay zeka analiz ediyor…</>
                  ) : aiStage === "error" ? (
                    <><X size={13} className="text-rose-600"/> İşlem başarısız oldu</>
                  ) : (
                    <>{aiPreviews.length} fotoğraf seçildi</>
                  )}
                </div>
                {!aiBusy && (
                  <button
                    type="button"
                    onClick={clearAiPreviews}
                    className="text-[11px] text-slate-500 hover:text-brand-orange inline-flex items-center gap-1"
                    data-testid="program-ai-progress-clear"
                  >
                    <X size={11}/> Temizle
                  </button>
                )}
              </div>

              {/* Progress bar. In 'analyzing' stage we show an animated
                  indeterminate stripe since we don't know how long the LLM
                  will take. */}
              <div className="mt-2 h-2 rounded-full bg-brand-orange/10 overflow-hidden" role="progressbar" aria-valuenow={aiPct} aria-valuemin={0} aria-valuemax={100}>
                {aiStage === "analyzing" ? (
                  <div className="h-full w-1/3 bg-gradient-to-r from-brand-orange via-brand-gold to-brand-orange animate-[shimmer_1.2s_ease-in-out_infinite]" style={{
                    animation: "aiShimmer 1.2s linear infinite",
                  }} />
                ) : (
                  <div
                    className={`h-full transition-all duration-200 ${
                      aiStage === "error" ? "bg-rose-500" : aiStage === "done" ? "bg-emerald-500" : "bg-gradient-to-r from-brand-orange to-brand-orange-dark"
                    }`}
                    style={{ width: `${aiPct}%` }}
                    data-testid="program-ai-progress-bar"
                  />
                )}
              </div>

              {/* Per-file thumbnails so the agent visually confirms what
                  they uploaded — much clearer than a filename list. */}
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="program-ai-thumbs">
                {aiPreviews.map((p, i) => (
                  <div
                    key={i}
                    className="relative aspect-square rounded-lg overflow-hidden border border-brand-navy/10 bg-slate-50"
                    title={p.name}
                  >
                    <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                    {aiStage === "done" && (
                      <div className="absolute inset-0 bg-emerald-500/70 flex items-center justify-center">
                        <CheckCircle2 size={20} className="text-white drop-shadow" />
                      </div>
                    )}
                    {(aiStage === "uploading" || aiStage === "analyzing") && (
                      <div className="absolute inset-0 bg-brand-navy/50 flex items-center justify-center">
                        <Loader2 size={20} className="text-white animate-spin drop-shadow" />
                      </div>
                    )}
                    <div className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[9px] px-1 py-0.5 truncate">
                      {p.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Prefill from an existing reservation */}
        <div className="bg-gradient-to-br from-brand-navy/5 via-white to-brand-navy/5 border border-brand-navy/20 rounded-2xl p-4 mt-4" data-testid="program-prefill">
          <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold">Hızlı Doldur</div>
          <div className="text-sm text-slate-700 mt-1">
            Mevcut bir rezervasyondan otel, grup adı ve tarihleri otomatik alın:
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <select
              value={pickedResId}
              onChange={(e) => applyReservation(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-transparent px-3 py-2 text-sm flex-1 min-w-[220px]"
              data-testid="program-reservation-picker"
            >
              <option value="">— Rezervasyon seçin —</option>
              {reservations.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.reservation_number} · {r.group_name} · {formatTr(r.checkin)} → {formatTr(r.checkout)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Editor: header inputs */}
        <div className="bg-white border border-brand-navy/10 rounded-2xl p-5 mt-4 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="flex items-center gap-1"><User size={12}/> Grup / Müşteri Adı <span className="text-slate-400 font-normal">(opsiyonel)</span></Label>
            <Input value={header.group_name} onChange={(e) => setHeaderField("group_name", e.target.value)} placeholder="Boş bırakabilirsiniz" data-testid="program-group-name" />
          </div>
          <div className="sm:col-span-2">
            <Label className="flex items-center gap-1"><Hotel size={12}/> Otel</Label>
            <Input value={header.hotel_name} onChange={(e) => setHeaderField("hotel_name", e.target.value)} data-testid="program-hotel" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><User size={12}/> Grup Hocası</Label>
            <Input value={header.guide_name} onChange={(e) => setHeaderField("guide_name", e.target.value)} data-testid="program-guide-name" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><Phone size={12}/> Grup Hocası Telefon</Label>
            <Input value={header.guide_phone} onChange={(e) => setHeaderField("guide_phone", e.target.value)} placeholder="+90 5xx xxx xx xx" data-testid="program-guide-phone" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><User size={12}/> Otel Görevlisi</Label>
            <Input value={header.hotel_rep_name} onChange={(e) => setHeaderField("hotel_rep_name", e.target.value)} data-testid="program-hotel-rep-name" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><Phone size={12}/> Otel Görevlisi Telefon</Label>
            <Input value={header.hotel_rep_phone} onChange={(e) => setHeaderField("hotel_rep_phone", e.target.value)} placeholder="+966 5x xxx xxxx" data-testid="program-hotel-rep-phone" />
          </div>
          <div className="sm:col-span-2">
            <Label className="flex items-center gap-1"><Bus size={12}/> Ulaşım Notu</Label>
            <Input value={header.transport_note} onChange={(e) => setHeaderField("transport_note", e.target.value)} placeholder="Havaalanı transferi, otobüs firması vb." data-testid="program-transport" />
          </div>
          <div>
            <Label>Giriş Tarihi *</Label>
            <Input type="date" value={header.checkin} onChange={(e) => setHeaderField("checkin", e.target.value)} data-testid="program-checkin" />
          </div>
          <div>
            <Label>Çıkış Tarihi *</Label>
            <Input type="date" value={header.checkout} onChange={(e) => setHeaderField("checkout", e.target.value)} data-testid="program-checkout" />
          </div>
          <div className="sm:col-span-2 text-xs text-slate-500">
            {totalNights > 0 ? <>Program otomatik olarak <b>{totalNights}</b> günlük oluşturuldu.</> : "Tarihleri girin, günlük kutucuklar otomatik oluşacak."}
          </div>
        </div>

        {/* Editor: days */}
        {!showPreview && (
        <div className="mt-4 grid gap-4" data-testid="program-days">
          {days.length === 0 && (
            <div className="bg-white border border-dashed border-brand-navy/20 rounded-2xl p-10 text-center text-slate-500 text-sm">
              Programı görmek için üstteki giriş/çıkış tarihlerini doldurun.
            </div>
          )}
          {days.map((d, i) => (
            <div key={d.date} className="bg-white border border-brand-navy/10 rounded-2xl p-5" data-testid={`program-day-${i}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-brand-orange font-semibold">Gün {i + 1}</div>
                  <div className="font-serif text-lg text-brand-navy">{formatTr(d.date)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    value={d.title}
                    onChange={(e) => setDayTitle(i, e.target.value)}
                    placeholder="Örn. Medine'ye geliş"
                    className="max-w-xs h-9"
                    data-testid={`program-day-title-${i}`}
                  />
                  {i + 1 < days.length && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => duplicateDay(i)}
                      className="text-xs"
                      data-testid={`program-day-duplicate-${i}`}
                    >
                      <Copy size={12} className="mr-1" /> Sonraki güne kopyala
                    </Button>
                  )}
                </div>
              </div>

              <div className="mt-3 divide-y divide-slate-100">
                {d.items.map((it, j) => (
                  <div key={j} className="py-2 grid grid-cols-[80px_1fr_1fr_auto] gap-2 items-center">
                    <Input
                      value={it.time}
                      onChange={(e) => setItem(i, j, { time: e.target.value })}
                      placeholder="06:00"
                      className="h-9 text-center font-mono"
                      data-testid={`program-item-time-${i}-${j}`}
                    />
                    <Input
                      value={it.activity}
                      onChange={(e) => setItem(i, j, { activity: e.target.value })}
                      placeholder="Aktivite"
                      className="h-9"
                      data-testid={`program-item-activity-${i}-${j}`}
                    />
                    <Input
                      value={it.note}
                      onChange={(e) => setItem(i, j, { note: e.target.value })}
                      placeholder="Not"
                      className="h-9"
                      data-testid={`program-item-note-${i}-${j}`}
                    />
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => moveItem(i, j, -1)} className="text-slate-400 hover:text-brand-navy p-1" title="Yukarı"><ArrowUp size={13}/></button>
                      <button type="button" onClick={() => moveItem(i, j, +1)} className="text-slate-400 hover:text-brand-navy p-1" title="Aşağı"><ArrowDown size={13}/></button>
                      <button type="button" onClick={() => removeItem(i, j)} className="text-rose-500 hover:text-rose-700 p-1" title="Sil"><Trash2 size={13}/></button>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addItem(i)}
                className="mt-2 border-brand-orange/30 hover:bg-brand-orange/10 text-xs"
                data-testid={`program-add-item-${i}`}
              >
                <Plus size={12} className="mr-1" /> Aktivite ekle
              </Button>
            </div>
          ))}
        </div>
        )}

        {/* Inline ceremonial preview — same layout as print output. Rendered
            inside a bordered box on screen so agencies can visually confirm
            the final look before hitting Yazdır. */}
        {showPreview && (
          <div
            className="mt-4 rounded-2xl border border-brand-gold/40 shadow-xl overflow-hidden"
            data-testid="program-inline-preview"
          >
            <IslamicPrintPreview header={header} days={days} totalNights={totalNights} />
          </div>
        )}
      </div>

      {/* Print-only preview — Islamic-inspired ceremonial layout */}
      <div ref={printRef} className="print-area hidden print:block" data-testid="program-print-preview">
        <IslamicPrintPreview header={header} days={days} totalNights={totalNights} />
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: white !important; }
          .print-area { display: block !important; }
          .print-area .day-block { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}


/**
 * IslamicPrintPreview — redesigned single-page A4 ceremonial layout.
 *
 * Design decisions (2026-02, iter 22):
 *   - Fits on a single A4 page for the typical 7-14 day Umre trip.
 *   - Only shows umre + ziyaret items (agency filters out prayer/meal times).
 *   - More decorative Islamic motifs: 8-point star border, Kaaba corners,
 *     mihrab-shaped section headers, geometric arabesque background wash.
 *   - Colorful palette: navy → gold → cream + a soft emerald pop for accents.
 *   - Adds "Grup Hocası" (guide) + "Otel Görevlisi" (hotel rep) contact rows
 *     so pilgrims can reach both parties on the ground.
 *   - Days rendered as compact chip-rows with numbered gold badges — scales
 *     from 3 to 15 days without breaking single-page layout.
 */
function IslamicPrintPreview({ header, days, totalNights }) {
  const NAVY = "#0F2544";
  const GOLD = "#C9A24A";
  const CREAM = "#FBF5EC";
  const ORANGE = "#E27D3C";
  const EMERALD = "#2A7F5B";

  // Corner Kaaba silhouette + arabesque flourish (SVG).
  const CornerMotif = ({ flip, bottom }) => (
    <svg
      width="60" height="60" viewBox="0 0 60 60"
      style={{
        position: "absolute",
        [bottom ? "bottom" : "top"]: "-4mm",
        [flip ? "right" : "left"]: "-4mm",
        transform: flip ? (bottom ? "scale(-1,-1)" : "scaleX(-1)") : (bottom ? "scaleY(-1)" : "none"),
        opacity: 0.55,
      }}
      aria-hidden="true"
    >
      {/* Kaaba silhouette */}
      <rect x="8" y="25" width="26" height="20" fill={GOLD} opacity="0.85"/>
      <path d="M8 25 l13 -8 l13 8 z" fill={GOLD} opacity="0.85"/>
      <rect x="9" y="30" width="24" height="4" fill={NAVY} opacity="0.9"/>
      {/* Arabesque swirl */}
      <path d="M42 50 Q52 45 56 30 Q58 20 50 12" stroke={GOLD} strokeWidth="1" fill="none"/>
      <circle cx="56" cy="30" r="1.5" fill={GOLD}/>
      <path d="M46 8 l1.4 3.5 l3.6 .4 l-2.7 2.4 l.8 3.7 l-3.1-1.9 l-3.1 1.9 l.8-3.7 l-2.7-2.4 l3.6 -.4 z" fill={GOLD}/>
    </svg>
  );

  // 8-point Islamic star as tiny decoration on labels.
  const StarDot = ({ color = GOLD, size = 8 }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" style={{ display: "inline-block", verticalAlign: "middle" }}>
      <path d="M8 0 l1.8 4.8 l4.8 1.8 l-4.8 1.8 l-1.8 4.8 l-1.8-4.8 l-4.8-1.8 l4.8-1.8 z M8 2.3 l1.2 3.2 l3.2 1.2 l-3.2 1.2 l-1.2 3.2 l-1.2-3.2 l-3.2-1.2 l3.2-1.2 z" fill={color}/>
    </svg>
  );

  return (
    <div style={{
      background: CREAM,
      padding: "4mm",
      fontFamily: "'Playfair Display', 'Cairo', serif",
      color: NAVY,
      position: "relative",
      minHeight: "277mm", // A4 usable minus margins
    }}>
      {/* Arabesque background wash — subtle geometric tiling */}
      <div style={{
        position: "absolute", inset: 0, opacity: 0.04, pointerEvents: "none",
        backgroundImage: `radial-gradient(circle at 10% 10%, ${GOLD} 1px, transparent 2px), radial-gradient(circle at 50% 50%, ${NAVY} 1px, transparent 2px)`,
        backgroundSize: "18mm 18mm, 24mm 24mm",
      }}/>

      {/* Outer ornamental frame */}
      <div style={{
        border: `2px solid ${GOLD}`,
        borderRadius: "4mm",
        padding: "5mm 5mm 4mm",
        background: "white",
        position: "relative",
        boxShadow: `inset 0 0 0 1mm white, inset 0 0 0 1.2mm ${GOLD}55`,
      }}>
        <CornerMotif />
        <CornerMotif flip />
        <CornerMotif bottom />
        <CornerMotif flip bottom />

        {/* ============ HEADER BAND ============ */}
        <header style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #17335f 40%, ${NAVY} 100%)`,
          color: "white",
          borderRadius: "3mm",
          padding: "4mm 6mm",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}>
          {/* Bismillah — smaller than before to save vertical space */}
          <div style={{
            fontFamily: "'Cairo', 'Amiri', serif",
            fontSize: "18pt",
            color: GOLD,
            fontWeight: 700,
            lineHeight: 1.1,
            direction: "rtl",
          }}>
            بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
          </div>
          <div style={{ fontSize: "7pt", color: "rgba(255,255,255,0.75)", letterSpacing: "0.25em", marginTop: "0.5mm", textTransform: "uppercase" }}>
            Rahman ve Rahim olan Allah'ın adıyla
          </div>

          {/* Mini golden divider */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", margin: "2.5mm 0" }}>
            <div style={{ height: "1px", background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`, flex: 1, maxWidth: "20mm" }} />
            <StarDot color={GOLD} size={10} />
            <div style={{ height: "1px", background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`, flex: 1, maxWidth: "20mm" }} />
          </div>

          <div style={{ fontSize: "8pt", letterSpacing: "0.4em", color: GOLD, fontWeight: 600, textTransform: "uppercase" }}>Umre Grup Programı</div>
          {header.group_name && (
            <h1 style={{ fontSize: "18pt", fontWeight: 700, margin: "1mm 0 0.5mm", lineHeight: 1.1 }}>
              {header.group_name}
            </h1>
          )}
          {header.hotel_name && (
            <div style={{ fontSize: "10pt", color: "rgba(255,255,255,0.92)", fontStyle: "italic", marginTop: "0.5mm" }}>
              {header.hotel_name}
            </div>
          )}
        </header>

        {/* ============ META STRIP: dates + contacts ============ */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "2mm",
          marginTop: "3mm",
        }}>
          <MetaCell color={GOLD} label="Giriş" value={header.checkin ? formatTr(header.checkin) : "—"} />
          <MetaCell color={EMERALD} label="Süre" value={totalNights > 0 ? `${totalNights} gece` : "—"} />
          <MetaCell color={ORANGE} label="Çıkış" value={header.checkout ? formatTr(header.checkout) : "—"} />
        </div>

        {/* Contact rows */}
        {(header.guide_name || header.guide_phone || header.hotel_rep_name || header.hotel_rep_phone) && (
          <div style={{
            marginTop: "3mm",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "2mm",
          }}>
            <ContactCell
              accent={NAVY}
              label="Grup Hocası"
              name={header.guide_name}
              phone={header.guide_phone}
            />
            <ContactCell
              accent={EMERALD}
              label="Otel Görevlisi"
              name={header.hotel_rep_name}
              phone={header.hotel_rep_phone}
            />
          </div>
        )}

        {header.transport_note && (
          <div style={{
            marginTop: "2mm",
            padding: "1.5mm 3mm",
            background: `${GOLD}15`,
            border: `1px solid ${GOLD}55`,
            borderRadius: "2mm",
            fontSize: "8.5pt",
            color: NAVY,
            textAlign: "center",
          }}>
            <b style={{ color: ORANGE }}>Ulaşım:</b> {header.transport_note}
          </div>
        )}

        {/* ============ MIHRAB SECTION HEADER ============ */}
        <div style={{ margin: "4mm 0 3mm", textAlign: "center", position: "relative" }}>
          <MihrabBadge color={GOLD} label="Günlük Program" />
        </div>

        {/* ============ DAYS GRID — compact chip cards ============ */}
        <div style={{
          display: "grid",
          gridTemplateColumns: days.length > 6 ? "1fr 1fr" : "1fr",
          gap: "2mm",
        }}>
          {days.map((d, i) => (
            <DayCard
              key={d.date || i}
              index={i + 1}
              date={d.date}
              title={d.title}
              items={d.items}
              navy={NAVY}
              gold={GOLD}
              emerald={EMERALD}
              orange={ORANGE}
            />
          ))}
        </div>

        {/* ============ FOOTER ============ */}
        <footer style={{ marginTop: "4mm", textAlign: "center" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", margin: "1mm 0" }}>
            <div style={{ height: "1px", background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`, flex: 1, maxWidth: "30mm" }} />
            <div style={{ margin: "0 3mm", fontSize: "8pt", color: GOLD, letterSpacing: "0.3em", fontWeight: 700 }}>
              <StarDot color={GOLD} size={7} /> ARİAF <StarDot color={GOLD} size={7} />
            </div>
            <div style={{ height: "1px", background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`, flex: 1, maxWidth: "30mm" }} />
          </div>
          <div style={{ fontFamily: "'Cairo', 'Amiri', serif", fontSize: "10pt", color: NAVY, fontWeight: 600, direction: "rtl", marginTop: "1mm" }}>
            رَبَّنَا تَقَبَّلْ مِنَّا
          </div>
          <div style={{ fontSize: "7pt", color: NAVY, opacity: 0.6, fontStyle: "italic", marginTop: "0.5mm" }}>
            "Rabbimiz, bizden kabul buyur"
          </div>
          <div style={{ fontSize: "7pt", color: NAVY, opacity: 0.5, marginTop: "0.5mm" }}>
            Ariaf Catering · Umre yolcularına vatan sofrası
          </div>
        </footer>
      </div>
    </div>
  );
}

/** MetaCell — colorful pill for a single header metric (date / duration). */
function MetaCell({ label, value, color }) {
  return (
    <div style={{
      background: "white",
      border: `1.5px solid ${color}66`,
      borderTop: `3px solid ${color}`,
      borderRadius: "2mm",
      padding: "2mm 3mm",
      textAlign: "center",
    }}>
      <div style={{ fontSize: "7pt", letterSpacing: "0.3em", color, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: "11pt", color: "#0F2544", fontWeight: 700, marginTop: "0.5mm" }}>{value}</div>
    </div>
  );
}

/** ContactCell — labelled contact row for guide / hotel rep. */
function ContactCell({ label, name, phone, accent }) {
  return (
    <div style={{
      background: "white",
      border: `1px solid ${accent}44`,
      borderLeft: `3px solid ${accent}`,
      borderRadius: "2mm",
      padding: "2mm 3mm",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "2mm",
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "7pt", letterSpacing: "0.25em", color: accent, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontSize: "9.5pt", color: "#0F2544", fontWeight: 600, marginTop: "0.3mm", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {name || <span style={{ color: "#94a3b8", fontStyle: "italic", fontWeight: 400 }}>—</span>}
        </div>
      </div>
      {phone && (
        <div style={{
          background: `${accent}15`,
          color: accent,
          borderRadius: "1.5mm",
          padding: "1mm 2mm",
          fontFamily: "'Outfit', monospace",
          fontSize: "8.5pt",
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}>
          {phone}
        </div>
      )}
    </div>
  );
}

/** MihrabBadge — arch-shaped section label evoking a mosque niche. */
function MihrabBadge({ label, color }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "2mm" }}>
      <svg width="14" height="18" viewBox="0 0 14 18">
        <path d="M1 17 L1 8 A6 6 0 0 1 13 8 L13 17 Z" fill={color}/>
      </svg>
      <span style={{ fontSize: "10pt", letterSpacing: "0.3em", color: "#0F2544", fontWeight: 700, textTransform: "uppercase" }}>{label}</span>
      <svg width="14" height="18" viewBox="0 0 14 18" style={{ transform: "scaleX(-1)" }}>
        <path d="M1 17 L1 8 A6 6 0 0 1 13 8 L13 17 Z" fill={color}/>
      </svg>
    </div>
  );
}

/** DayCard — compact day block with numbered gold badge + item list. */
function DayCard({ index, date, title, items, navy, gold, emerald, orange }) {
  return (
    <div
      className="day-block"
      style={{
        background: "white",
        border: `1px solid ${gold}55`,
        borderRadius: "2mm",
        overflow: "hidden",
        boxShadow: `0 1mm 2mm rgba(15, 37, 68, 0.05)`,
      }}
    >
      {/* Day header — colored gradient with numbered badge */}
      <div style={{
        background: `linear-gradient(90deg, ${navy}, ${navy} 50%, ${emerald} 100%)`,
        color: "white",
        padding: "1.5mm 3mm",
        display: "flex",
        alignItems: "center",
        gap: "2mm",
      }}>
        <div style={{
          width: "7mm", height: "7mm", borderRadius: "50%",
          background: gold, color: navy,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800, fontSize: "10pt", fontFamily: "'Playfair Display', serif",
          flexShrink: 0,
          boxShadow: "0 0.5mm 1mm rgba(0,0,0,0.15)",
        }}>
          {index}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "6.5pt", letterSpacing: "0.35em", color: gold, textTransform: "uppercase", fontWeight: 700 }}>
            Gün {index}
          </div>
          <div style={{ fontSize: "9.5pt", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {date ? formatTr(date) : "—"}{title ? ` · ${title}` : ""}
          </div>
        </div>
      </div>

      {/* Item rows — only umre & ziyaret; agencies filter out prayers/meals */}
      <div>
        {items.length === 0 && (
          <div style={{ padding: "2mm 3mm", fontSize: "8pt", color: "#94a3b8", fontStyle: "italic" }}>
            Bu gün için aktivite belirtilmedi.
          </div>
        )}
        {items.map((it, j) => (
          <div
            key={j}
            style={{
              padding: "1.5mm 3mm",
              borderTop: j === 0 ? "none" : `1px dashed ${navy}15`,
              display: "grid",
              gridTemplateColumns: "12mm 1fr",
              gap: "2mm",
              alignItems: "start",
              background: j % 2 === 0 ? "transparent" : `${gold}08`,
            }}
          >
            <div style={{
              fontFamily: "'Outfit', monospace",
              color: orange,
              fontWeight: 800,
              fontSize: "9pt",
              textAlign: "right",
              paddingTop: "0.3mm",
            }}>
              {it.time || "—"}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "9pt", fontWeight: 600, color: navy, lineHeight: 1.3 }}>
                {it.activity || "—"}
              </div>
              {it.note && (
                <div style={{ fontSize: "7.5pt", color: navy, opacity: 0.6, fontStyle: "italic", marginTop: "0.3mm", lineHeight: 1.2 }}>
                  {it.note}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
